import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Fingerprint, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useBiometricLock } from '@/hooks/useBiometricLock'
import {
  consumeHandoffOverstay,
  isNativeUiHandoffInProgress,
  noteHandoffBackgrounded,
} from '@/libs/nativeUiHandoff'
import { useAppSelector } from '@/store/hooks'

/**
 * Opt-in biometric app-lock overlay. No-ops entirely (renders nothing, registers no
 * listeners) unless running natively, the user has turned the lock on (Account >
 * Security), and they're authenticated. Deliberately independent of `NativeBridge` —
 * that component has no render output today and shouldn't grow feature-specific
 * responsibilities — so this owns its own `appStateChange` subscription (Capacitor
 * supports multiple listeners on the same event without conflict).
 */
export function AppLockGate() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { logout } = useAuth()
  const { isAvailable, enabled, locked, lock, verify } = useBiometricLock()
  const [verifying, setVerifying] = useState(false)
  const [verificationFailed, setVerificationFailed] = useState(false)

  // Requiring `isAvailable` means that if biometric hardware/enrollment becomes
  // unavailable after the user turned the toggle on, the gate fails open (not locked)
  // instead of failing closed (permanently locked with no way to reach the Account
  // toggle that would turn it back off) — mirrors the toggle's own visibility check.
  const isGateActive = Capacitor.isNativePlatform() && enabled && isAvailable && isAuthenticated

  useEffect(() => {
    if (!isGateActive) return

    // Cold start (or the moment the gate becomes active, e.g. the user just turned the
    // toggle on): require verification before the app is usable.
    lock()

    const listenerPromise = CapacitorApp.addListener('appStateChange', (state) => {
      if (!state.isActive) {
        // Skip locking immediately while a native picker/share sheet hand-off is in
        // progress — those legitimately background the app too, but aren't the user
        // leaving. Still remember that a backgrounding happened, in case the hand-off
        // turns out to be a long one (see `nativeUiHandoff`).
        if (isNativeUiHandoffInProgress()) {
          noteHandoffBackgrounded()
          return
        }
        lock()
        return
      }

      // Resumed. If we were mid hand-off (or just finished one) and it backgrounded the
      // app for longer than the "quick picker" threshold, it's no longer distinguishable
      // from the user genuinely leaving — lock retroactively now that we're back.
      if (consumeHandoffOverstay()) lock()
    })

    return () => {
      void listenerPromise.then((listener) => listener.remove())
    }
  }, [isGateActive, lock])

  if (!isGateActive || !locked) return null

  const handleUnlock = async () => {
    setVerifying(true)
    try {
      await verify()
      setVerificationFailed(false)
    } catch {
      setVerificationFailed(true)
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <Fingerprint className="h-16 w-16 text-primary" />
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t('appLock.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('appLock.description')}</p>
        {verificationFailed && (
          <p className="text-sm text-destructive">{t('appLock.verificationFailed')}</p>
        )}
      </div>
      <div className="flex w-full max-w-xs flex-col gap-2">
        <Button onClick={() => void handleUnlock()} disabled={verifying}>
          {verificationFailed ? t('appLock.retry') : t('appLock.unlock')}
        </Button>
        {verificationFailed && (
          <Button variant="outline" onClick={() => void logout()}>
            <LogOut className="w-4 h-4 mr-2" />
            {t('appLock.logOut')}
          </Button>
        )}
      </div>
    </div>
  )
}
